import BaseButton from 'components/ui/BaseButton';
import ValidationErrors from 'components/setting-generator-revamp/tabs-revamp/tab-actions/ValidationErrors';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { upsertFocusModeTemplate } from 'store/reducer/focus-mode/extra';
import { upsertHabitPack } from 'store/reducer/habit-pack/extra';
import { tabActionsSelector } from 'store/reducer/setting/selectors';
import { filterOutCutoffTimeAndTutorial } from 'utils/helpers';
import { filterRoutinesInvalidActivities } from 'utils/settings.util';
import { hasBlankHabitsDroppedOnSave } from 'utils/habit-validation.util';
import { hasPackEditorUnsavedChanges } from 'utils/pack-editor.util';
import { PackEditorSaveHandler } from 'interfaces/marketplace.interface';
import { PackAndFocusEntityType, PackEditorMode } from 'enums';
import { usePackAndFocusEditor } from '../../hooks/usePackAndFocusEditor';

interface EditorFooterProps {
  onSaveSuccess: () => void;
  registerSaveHandler: (handler: PackEditorSaveHandler | null) => void;
}

const EditorFooter = ({
  onSaveSuccess,
  registerSaveHandler
}: EditorFooterProps) => {
  const dispatch = useAppDispatch();
  const { closeEditor } = usePackAndFocusEditor();
  const {
    currentSettings,
    errors,
    areSettingsValid,
    currentViewedFocusTemplate,
    areSettingsSaving,
    isUserUpsertingPack,
    initialCurrentViewedPack,
    initialCurrentViewedFocusTemplate,
    editorType,
    editorMode,
    packEditorTabMode
  } = useAppSelectorShallow((state) => ({
    ...tabActionsSelector(state),
    editorType: state.habitPack.editorType,
    editorMode: state.habitPack.editorMode,
    packEditorTabMode: state.habitPack.packEditorTabMode,
    initialCurrentViewedPack: state.habitPack.initialCurrentViewedPack,
    initialCurrentViewedFocusTemplate:
      state.focusMode.initialCurrentViewedFocusTemplate
  }));

  const isSettingsValidClean = areSettingsValid && !(errors?.length ?? 0);
  const hasBlankHabitsToDrop = useMemo(
    () => hasBlankHabitsDroppedOnSave(currentSettings),
    [currentSettings]
  );

  const isDirty = useMemo(
    () =>
      hasPackEditorUnsavedChanges({
        packEditorTabMode,
        currentSettings,
        initialCurrentViewedPack,
        initialCurrentViewedFocusTemplate,
        currentViewedFocusTemplate
      }),
    [
      packEditorTabMode,
      currentSettings,
      initialCurrentViewedPack,
      initialCurrentViewedFocusTemplate,
      currentViewedFocusTemplate
    ]
  );

  const hasBlockingValidationErrors = (errors?.length ?? 0) > 0;
  const canShowSave =
    isSettingsValidClean &&
    (editorMode === PackEditorMode.CREATE || isDirty || hasBlankHabitsToDrop);

  const showActionsRow = hasBlockingValidationErrors || canShowSave;

  const [morning_habits, break_habits] = useMemo(
    () =>
      filterOutCutoffTimeAndTutorial(
        currentSettings.morning_activities ?? [],
        currentSettings.break_activities ?? []
      ),
    [currentSettings]
  );

  const handleSave = useCallback(async () => {
    if (editorType === PackAndFocusEntityType.HABIT_PACK) {
      const {
        morning_activities,
        break_activities,
        evening_activities,
        custom_routines
      } = filterRoutinesInvalidActivities(
        morning_habits,
        break_habits,
        currentSettings.evening_activities ?? [],
        currentSettings.custom_routines ?? []
      );

      const packPayload = {
        ...currentSettings,
        morning_activities,
        break_activities,
        evening_activities,
        custom_routines
      };

      const result = await dispatch(upsertHabitPack(packPayload));
      if (upsertHabitPack.fulfilled.match(result) && result.payload?.success) {
        toast.success(t('settings_saved'));
        onSaveSuccess();
        closeEditor();
      }
      return;
    }

    if (
      editorType === PackAndFocusEntityType.FOCUS_TEMPLATE &&
      currentViewedFocusTemplate
    ) {
      const result = await dispatch(
        upsertFocusModeTemplate(currentViewedFocusTemplate)
      );
      if (
        upsertFocusModeTemplate.fulfilled.match(result) &&
        result.payload?.success
      ) {
        toast.success(t('settings_saved'));
        onSaveSuccess();
        closeEditor();
      }
    }
  }, [
    break_habits,
    closeEditor,
    currentSettings,
    currentViewedFocusTemplate,
    dispatch,
    editorType,
    morning_habits,
    onSaveSuccess
  ]);

  useEffect(() => {
    if (!canShowSave) {
      registerSaveHandler(null);
      return;
    }

    registerSaveHandler(handleSave);
    return () => registerSaveHandler(null);
  }, [canShowSave, handleSave, registerSaveHandler]);

  if (!showActionsRow) {
    return null;
  }

  return (
    <div className='w-full shrink-0 inline-flex items-end justify-center ~gap-5/10 ~pt-3/6 border-t border-primary-200 dark:border-secondary-dark-500 ~px-4/6 ~pb-4/6'>
      {hasBlockingValidationErrors ? <ValidationErrors /> : null}
      {canShowSave ? (
        <>
          <BaseButton
            variant='secondary'
            title={t('btn_cancel')}
            onClick={closeEditor}
            disableDarkMode
            size='sm'
          />
          <BaseButton
            onClick={handleSave}
            title={t('save')}
            disableDarkMode
            data-testid='pack-editor-save-button'
            additionalStyles='~px-5/10'
            isLoading={areSettingsSaving || isUserUpsertingPack}
            size='sm'
          />
        </>
      ) : null}
    </div>
  );
};

export default EditorFooter;
